import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFetchOrganizationUsers from "./useFetchOrganizationUsers";
import { RootStateOrAny } from "react-redux";
import { User } from "types";

interface UseGetOrganizationUsersProps {
  organizationId: string | null;
  sort?: boolean;
  userId?: string;
}

const useGetOrganizationUsers = ({
  organizationId,
  sort = false,
  userId,
}: UseGetOrganizationUsersProps) => {
  const navigate = useNavigate();
  const { fetchOrganizationUsers } = useFetchOrganizationUsers();
  const organizationUsers = useSelector(
    (state: RootStateOrAny) => state.organizations.organizationUsers
  );
  const lastOrganizationId = useRef<string | null>(null);

  useEffect(() => {
    if (organizationId && organizationId !== lastOrganizationId.current) {
      lastOrganizationId.current = organizationId;
      fetchOrganizationUsers(organizationId).catch((error: any) => {
        // Handle permission errors gracefully - just log them, don't redirect
        if (
          error?.message?.includes("ORGST_6041") ||
          error?.message?.includes("ORGST_6040") ||
          error?.message?.includes("ORGST_6030") ||
          error?.message?.includes("403")
        ) {
          // User doesn't have permission to view organization members
          // This is expected for non-owners, so we don't show an error or redirect
          console.log(
            "User doesn't have permission to view organization members"
          );
        } else {
          // For other errors, show a toast but don't redirect
          console.error("Error fetching organization users:", error);
        }
      });
    }
  }, [organizationId, fetchOrganizationUsers, navigate]);

  const sortByRolePriority = (users: User[]) => {
    const rolePriority: { [key: string]: number } = {
      owner: 1,
      moderator: 2,
      member: 3,
    };

    return users
      .slice()
      .sort(
        (a, b) =>
          rolePriority[a.role as string] - rolePriority[b.role as string]
      );
  };

  const organizationUser = organizationUsers.find(
    (user: User) => user.uuid === userId
  );

  if (!organizationUsers) {
    return { organizationUsers: [] };
  }

  if (sort) {
    const sortedUsers = sortByRolePriority(organizationUsers);
    return { organizationUsers: sortedUsers, organizationUser };
  }

  return { organizationUsers, organizationUser };
};

export default useGetOrganizationUsers;
